/**
 * SplashScreen.jsx — Marketing landing page
 *
 * Replaces the previous auto-redirect splash. Now serves as the
 * public-facing front door for localgenie.ai with:
 *   - Hero section (announcement badge, headline, subhead, CTAs, app mockup)
 *   - Numbered feature cards (no icons — modern typography-driven design)
 *   - Final CTA section
 *   - Minimal footer (no fake links — only real ones)
 *
 * Design adapted from v0.app generated mockup, but rewritten in plain
 * JSX + CSS using existing design tokens (no Tailwind, no shadcn,
 * no lucide-react). 2026-05-03 Sunday polish session.
 *
 * "Try LocalGenie" CTAs route directly to /home (the AIChat new UI).
 * No signup/signin layer — anonymous use, sessionId managed by Redis
 * on the backend. Login/persona routes were removed in this same commit.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import './SplashScreen.css';

/**
 * LocalGenie logo — MapPin + Sparkles composition matching v0.app design.
 * Inline SVG (no lucide-react dependency). Uses currentColor so it adapts
 * to whatever color is set on the parent element.
 */
const LogoIcon = ({ size = 28 }) => (
  <span
    className="splash-logo-svg-wrap"
    aria-hidden="true"
    style={{ width: size, height: size }}
  >
    {/* Map pin — main icon */}
    <svg
      className="splash-logo-pin"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path
        d="M20 10c0 7-8 13-8 13s-8-6-8-13a8 8 0 0 1 16 0Z"
        fill="currentColor"
        fillOpacity="0.15"
      />
      <circle cx="12" cy="10" r="3" fill="currentColor" />
    </svg>
    {/* Sparkles — small star overlay at top-right */}
    <svg
      className="splash-logo-sparkle"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#475569"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path
        d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"
        fill="rgba(100, 116, 139, 0.3)"
      />
      <path d="M20 3v4" />
      <path d="M22 5h-4" />
      <path d="M4 17v2" />
      <path d="M5 18H3" />
    </svg>
  </span>
);

const SplashScreen = () => {
  // ─── Feature data (numbered cards, no icons) ─────────────────────────────
  const features = [
    {
      number: '01',
      title: 'Multi-Agent AI',
      description:
        'Five specialist agents — ConversationAgent, SearchAgent, AnalysisAgent, ExplanationAgent, SupervisorAgent — working together to understand your needs and rank neighborhoods.'
    },
    {
      number: '02',
      title: 'Conversational Discovery',
      description:
        'Just describe your life. No filters, no listings — just a natural conversation that captures budget, work commute, family situation, and lifestyle priorities.'
    },
    {
      number: '03',
      title: 'Real Resident Reviews',
      description:
        'RAG-powered semantic search surfaces real-resident-voice quotes on every recommendation card. See what people who actually live there say.'
    },
    {
      number: '04',
      title: 'Real-Time Progress',
      description:
        'WebSocket-powered live updates as the agents search and analyze. Watch your recommendations come together — no spinner-of-doom waiting.'
    },
    {
      number: '05',
      title: 'Personalized Match Scores',
      description:
        'Every neighborhood gets a fit score based on YOUR priorities — not a generic "popular" ranking. The Domain might be 87% for one person and 51% for another.'
    },
    {
      number: '06',
      title: 'Real Cities, Real Data',
      description:
        '5 major US cities, 22 hand-curated neighborhoods, 66 seeded resident reviews. Live now, growing as users contribute their own experiences.'
    }
  ];

  return (
    <div className="splash-page">
      {/* ─── NAVIGATION (fixed top, pill-shaped) ───────────────────────── */}
      <header className="splash-nav-wrap">
        <nav className="splash-nav">
          <Link to="/" className="splash-logo">
            <LogoIcon size={28} />
            <span className="splash-logo-text">
              LocalGenie<span className="splash-logo-accent">.ai</span>
            </span>
          </Link>
          <div className="splash-nav-links">
            <a href="#features" className="splash-nav-link">Features</a>
            <a
              href="https://github.com/karankappal5/LocalGenie-Enhanced-v2"
              target="_blank"
              rel="noopener noreferrer"
              className="splash-nav-link"
            >
              GitHub
            </a>
          </div>
          <Link to="/home" className="splash-nav-cta">
            Try Now →
          </Link>
        </nav>
      </header>

      {/* ─── HERO ──────────────────────────────────────────────────────── */}
      <section className="splash-hero">
        {/* Background glow */}
        <div className="splash-hero-glow" aria-hidden="true" />

        {/* Announcement badge */}
        <div className="splash-hero-badge">
          <span className="splash-hero-badge-dot" aria-hidden="true">✨</span>
          <span>AI-powered neighborhood matching is live</span>
          <span aria-hidden="true">→</span>
        </div>

        {/* Headline */}
        <h1 className="splash-hero-headline">
          Find your perfect{' '}
          <span className="splash-hero-headline-accent">neighborhood.</span>
        </h1>

        {/* Subheadline */}
        <p className="splash-hero-subhead">
          Multi-agent AI analyzes your lifestyle, work commute, and priorities —
          then matches you with neighborhoods backed by real resident reviews.
        </p>

        {/* CTAs */}
        <div className="splash-hero-ctas">
          <Link to="/home" className="splash-cta splash-cta-primary">
            Try LocalGenie <span aria-hidden="true">→</span>
          </Link>
          <a href="#features" className="splash-cta splash-cta-secondary">
            See how it works
          </a>
        </div>

        {/* App mockup — browser chrome + chat preview */}
        <div className="splash-hero-mockup">
          <div className="splash-mockup-frame">
            {/* Browser chrome bar */}
            <div className="splash-mockup-chrome">
              <div className="splash-mockup-dots">
                <span className="splash-mockup-dot splash-mockup-dot-red" />
                <span className="splash-mockup-dot splash-mockup-dot-yellow" />
                <span className="splash-mockup-dot splash-mockup-dot-green" />
              </div>
              <div className="splash-mockup-url">localgenie.ai/home</div>
            </div>

            {/* App content preview */}
            <div className="splash-mockup-content">
              <div className="splash-mockup-chat">
                {/* User message */}
                <div className="splash-mockup-message splash-mockup-message-user">
                  <div className="splash-mockup-bubble splash-mockup-bubble-user">
                    Looking to relocate to Austin with my family. Budget $4K/mo.
                    Schools matter most.
                  </div>
                </div>
                {/* AI message */}
                <div className="splash-mockup-message splash-mockup-message-ai">
                  <div className="splash-mockup-avatar">AI</div>
                  <div className="splash-mockup-bubble splash-mockup-bubble-ai">
                    Austin is a fantastic choice for families. Based on your
                    priorities, I'd recommend{' '}
                    <strong>Mueller</strong>, <strong>Hyde Park</strong>, and{' '}
                    <strong>Crestview</strong> — all top-school zones with strong
                    family communities.
                  </div>
                </div>
              </div>

              {/* Match score pill at bottom */}
              <div className="splash-mockup-result">
                <span className="splash-mockup-result-score">87%</span>
                <span className="splash-mockup-result-label">
                  Top match: <strong>Mueller</strong> · Schools 9.4/10 · Walkable
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── STATS BAR ─────────────────────────────────────────────────── */}
      <section className="splash-stats">
        <span className="splash-stats-item">
          <strong>5</strong> cities
        </span>
        <span className="splash-stats-divider">•</span>
        <span className="splash-stats-item">
          <strong>22</strong> neighborhoods
        </span>
        <span className="splash-stats-divider">•</span>
        <span className="splash-stats-item">
          <strong>66</strong> resident reviews
        </span>
        <span className="splash-stats-divider">•</span>
        <span className="splash-stats-item">
          <strong>Live</strong> now
        </span>
      </section>

      {/* ─── FEATURES ──────────────────────────────────────────────────── */}
      <section className="splash-features" id="features">
        <div className="splash-features-header">
          <h2 className="splash-features-title">
            Why LocalGenie is different
          </h2>
          <p className="splash-features-subtitle">
            We built the system we wished existed when we were trying to find
            our own perfect neighborhood. No filter sliders. No endless listings.
            Just intelligent, multi-dimensional matching.
          </p>
        </div>

        <div className="splash-features-grid">
          {features.map((feature) => (
            <div key={feature.number} className="splash-feature-card">
              <div className="splash-feature-number">{feature.number}</div>
              <h3 className="splash-feature-title">{feature.title}</h3>
              <p className="splash-feature-description">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── FINAL CTA ─────────────────────────────────────────────────── */}
      <section className="splash-final-cta">
        <div className="splash-final-cta-card">
          <div className="splash-final-cta-glow" aria-hidden="true" />
          <h2 className="splash-final-cta-title">
            Ready to find your match?
          </h2>
          <p className="splash-final-cta-subtitle">
            Free. No signup. Just open and start chatting.
          </p>
          <Link to="/home" className="splash-cta splash-cta-primary splash-cta-large">
            Try LocalGenie <span aria-hidden="true">→</span>
          </Link>
        </div>
      </section>

      {/* ─── FOOTER ────────────────────────────────────────────────────── */}
      <footer className="splash-footer">
        <div className="splash-footer-content">
          {/* Brand block */}
          <div className="splash-footer-brand">
            <Link to="/" className="splash-logo">
              <LogoIcon size={28} />
              <span className="splash-logo-text">
                LocalGenie<span className="splash-logo-accent">.ai</span>
              </span>
            </Link>
            <p className="splash-footer-tagline">
              AI-powered neighborhood recommendations to help you find your
              perfect place.
            </p>
          </div>

          {/* Links */}
          <div className="splash-footer-links">
            <a
              href="https://github.com/karankappal5/LocalGenie-Enhanced-v2"
              target="_blank"
              rel="noopener noreferrer"
              className="splash-footer-link"
            >
              GitHub
            </a>
            <Link to="/submit-review" className="splash-footer-link">
              Submit a review
            </Link>
            {/* Site-level feedback (NPS + PMF) — Sunday polish 2026-05-03.
                Distinct from "Submit a review" which is neighborhood-level. */}
            <Link to="/feedback" className="splash-footer-link">
              Share feedback
            </Link>
            {/* LinkedIn placeholder — LG to add real URL once profile created */}
            {/* <a href="..." className="splash-footer-link">LinkedIn</a> */}
          </div>
        </div>

        <div className="splash-footer-bottom">
          <p className="splash-footer-copy">
            © {new Date().getFullYear()} LocalGenie. Powered by LocalGenie AI
            Multi-Agent System.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SplashScreen;
